import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #F9FAFB;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #148D89 0%, #1C64F2 100%);
  padding: 5rem 2rem;
  color: white;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 1rem;
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.95;
  max-width: 800px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: 4rem 2rem;
  background: white;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const Sidebar = styled.div`
  position: sticky;
  top: 100px;
  height: fit-content;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  
  h3 {
    color: #148D89;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1rem;
    color: #4B5563;
  }
  
  .price {
    font-size: 2rem;
    font-weight: 700;
    color: #148D89;
    margin: 1rem 0;
  }
`;


const Section = styled.div`
  margin-bottom: 3rem;
  
  h2 {
    color: #15243A;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 3px solid #148D89;
  }
  
  h3 {
    color: #148D89;
    margin: 1.5rem 0 1rem;
  }
  
  ul {
    list-style: none;
    padding: 0;
    
    li {
      padding: 0.75rem 0;
      padding-left: 2rem;
      position: relative;
      color: #4B5563;
      
      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #148D89;
        font-weight: bold;
        font-size: 1.2rem;
      }
    }
  }
`;

const ModuleCard = styled.div`
  background: #F9FAFB;
  border-left: 4px solid #148D89;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 10px;
  
  h4 {
    color: #15243A;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #6B7280;
    margin-bottom: 0.5rem;
  }
  
  .duration {
    font-weight: 600;
    color: #148D89;
  }
`;

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const BenefitCard = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 1.5rem;
  border-radius: 15px;
  text-align: center;
  
  .icon {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  h4 {
    color: #148D89;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #6B7280;
    font-size: 0.9rem;
  }
`;

const ComparisonTable = styled.table`
  width: 100%;
  margin-top: 1.5rem;
  border-collapse: collapse;
  
  th {
    background: #148D89;
    color: white;
    padding: 1rem;
    text-align: left;
  }
  
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #E5E7EB;
  }
  
  tr:nth-child(even) {
    background: #F9FAFB;
  }
`;

const AssistantMedical: React.FC = () => {
  return (
    <PageWrapper>
      <Helmet>
        <title>Formation Assistant Médical - Institut Genevois de Formation</title>
        <meta name="description" content="Devenez assistant(e) médical(e) et accompagnez les professionnels de santé dans leur pratique quotidienne. Formation pratique et théorique complète 100% en ligne." />
        <meta name="keywords" content="assistant médical, formation assistant médical, formation en ligne, assistance médicale, certification IGF, pratique médicale" />
      </Helmet>
      
      <HeroSection>
        <Container>
          <HeroTitle>Formation Assistant Médical</HeroTitle>
          <HeroSubtitle>
            Devenez un professionnel de l'assistance médicale et accompagnez les praticiens dans leur exercice quotidien
          </HeroSubtitle>
        </Container>
      </HeroSection>
      
      <ContentSection>
        <Container>
          <Grid>
            <MainContent>
              <Section>
                <h2>Présentation de la formation</h2>
                <p>
                  La formation d'Assistant Médical de l'Institut Genevois de Formation vous prépare à devenir un maillon essentiel 
                  de l'équipe médicale. Cette formation intensive de 6 mois, dispensée entièrement en ligne, vous permettra 
                  d'acquérir les compétences pratiques et théoriques nécessaires pour assister efficacement les professionnels de santé.
                </p>
                <p>
                  Notre approche pédagogique unique combine apprentissage théorique approfondi et mises en situation pratiques, 
                  vous préparant ainsi à répondre aux besoins réels du terrain médical.
                </p>
              </Section>
              
              <Section>
                <h2>Programme détaillé de la formation</h2>
                
                <ModuleCard>
                  <h4>Module 1 : Accueil et Communication Médicale</h4>
                  <p className="duration">Mois 1 à 2</p>
                  <p>Développez une communication professionnelle et empathique avec les patients.</p>
                  <ul>
                    <li>Accueil physique et téléphonique spécialisé</li>
                    <li>Communication thérapeutique</li>
                    <li>Gestion des situations d'urgence</li>
                    <li>Orientation et information des patients</li>
                    <li>Respect du secret médical</li>
                  </ul>
                </ModuleCard>
                
                <ModuleCard>
                  <h4>Module 2 : Assistance Médicale Pratique</h4>
                  <p className="duration">Mois 3 à 4</p>
                  <p>Maîtrisez les gestes techniques d'assistance aux praticiens.</p>
                  <ul>
                    <li>Installation et préparation des patients</li>
                    <li>Prise et surveillance des constantes vitales</li>
                    <li>Assistance aux examens médicaux</li>
                    <li>Préparation du matériel médical</li>
                    <li>Protocoles d'hygiène et asepsie</li>
                    <li>Gestion des prélèvements</li>
                  </ul>
                </ModuleCard>
                
                <ModuleCard>
                  <h4>Module 3 : Organisation et Gestion du Cabinet</h4>
                  <p className="duration">Mois 5 à 6</p>
                  <p>Optimisez l'organisation administrative et logistique du cabinet médical.</p>
                  <ul>
                    <li>Gestion avancée de l'agenda médical</li>
                    <li>Coordination des parcours de soins</li>
                    <li>Facturation et codification des actes</li>
                    <li>Télétransmission et relations avec les caisses</li>
                    <li>Gestion des stocks et commandes</li>
                    <li>Logiciels médicaux spécialisés</li>
                  </ul>
                </ModuleCard>
              </Section>
              
              <Section>
                <h2>Compétences développées</h2>
                <BenefitGrid>
                  <BenefitCard>
                    <div className="icon">🏥</div>
                    <h4>Assistance clinique</h4>
                    <p>Accompagnement du praticien lors des consultations et examens</p>
                  </BenefitCard>
                  <BenefitCard>
                    <div className="icon">💉</div>
                    <h4>Gestes techniques</h4>
                    <p>Réalisation d'actes techniques sous supervision médicale</p>
                  </BenefitCard>
                  <BenefitCard>
                    <div className="icon">📋</div>
                    <h4>Coordination des soins</h4>
                    <p>Organisation et suivi des parcours patients</p>
                  </BenefitCard>
                  <BenefitCard>
                    <div className="icon">🔬</div>
                    <h4>Préparation médicale</h4>
                    <p>Préparation des salles et du matériel médical</p>
                  </BenefitCard>
                  <BenefitCard>
                    <div className="icon">💻</div>
                    <h4>Outils numériques</h4>
                    <p>Maîtrise des logiciels médicaux et de télémédecine</p>
                  </BenefitCard>
                  <BenefitCard>
                    <div className="icon">🏢</div>
                    <h4>Gestion administrative</h4>
                    <p>Organisation complète du cabinet médical</p>
                  </BenefitCard>
                </BenefitGrid>
              </Section>
              
              <Section>
                <h2>Spécificités de la formation Assistant Médical</h2>
                <ComparisonTable>
                  <thead>
                    <tr>
                      <th>Domaine</th>
                      <th>Compétences acquises</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Accueil et communication</strong></td>
                      <td>90% de mise en situation validées, communication empathique, gestion des urgences</td>
                    </tr>
                    <tr>
                      <td><strong>Assistance médicale</strong></td>
                      <td>Prise de constantes, installation patients, assistance aux gestes médicaux</td>
                    </tr>
                    <tr>
                      <td><strong>Organisation administrative</strong></td>
                      <td>Gestion agenda, facturation, télétransmission, dossiers patients</td>
                    </tr>
                    <tr>
                      <td><strong>Hygiène et sécurité</strong></td>
                      <td>Protocoles d'asepsie, stérilisation, gestion des déchets médicaux</td>
                    </tr>
                    <tr>
                      <td><strong>Outils informatiques</strong></td>
                      <td>Logiciels médicaux, télémédecine, dossier patient informatisé</td>
                    </tr>
                  </tbody>
                </ComparisonTable>
              </Section>
              
              <Section>
                <h2>Méthode pédagogique innovante</h2>
                <p>
                  Notre formation s'appuie sur une pédagogie active et participative :
                </p>
                <ul>
                  <li>Vidéos pré-enregistrées accessibles 24h/24 sur notre plateforme</li>
                  <li>Sessions en visioconférence pour questions et corrections personnalisées</li>
                  <li>Exercices pratiques et mises en situation professionnelle</li>
                  <li>QCM d'évaluation continue pour valider vos acquis</li>
                  <li>Supports de cours téléchargeables hébergés sur Drive</li>
                  <li>Forum d'échange avec les formateurs et les autres apprenants</li>
                </ul>
              </Section>
              
              <Section>
                <h2>Débouchés et perspectives d'emploi</h2>
                <p>
                  Le métier d'Assistant Médical est en forte demande. Vous pourrez exercer dans :
                </p>
                <ul>
                  <li>Cabinets de médecine générale ou spécialisée</li>
                  <li>Centres de santé pluridisciplinaires</li>
                  <li>Maisons médicales de garde</li>
                  <li>Services hospitaliers ambulatoires</li>
                  <li>Cliniques privées</li>
                  <li>Centres de médecine du travail</li>
                  <li>Structures de télémédecine</li>
                  <li>EHPAD et structures médico-sociales</li>
                </ul>
              </Section>
              
              <Section>
                <h2>Évaluation et certification</h2>
                <p>
                  <strong>Évaluation continue :</strong> QCM et exercices pratiques tout au long de la formation
                </p>
                <p>
                  <strong>Examen final :</strong> Comprend un QCM théorique et une pratique simulée en visioconférence
                </p>
                <p>
                  <strong>Certification :</strong> Diplôme d'Assistant Médical délivré par l'Institut Genevois de Formation, 
                  attestant de vos compétences professionnelles et reconnu dans le secteur médical.
                </p>
                <p>
                  <strong>Taux de réussite :</strong> 95% de nos apprenants obtiennent leur certification du premier coup.
                </p>
              </Section>
            </MainContent>
            
            <Sidebar>
              <InfoCard>
                <h3>Informations pratiques</h3>
                <p><strong>Durée :</strong> 6 mois</p>
                <p><strong>Format :</strong> 100% en ligne</p>
                <p><strong>Niveau requis :</strong> Bac ou équivalent</p>
                <p><strong>Certification :</strong> Diplôme IGF reconnu</p>
                <p className="price">2000€</p>
                <p><small>Facilités de paiement disponibles</small></p>
              </InfoCard>
              
              <InfoCard>
                <h3>Sessions 2025</h3>
                <p>🎓 <strong>Janvier 2025</strong></p>
                <p>Début : 15 janvier</p>
                <p>🎓 <strong>Mars 2025</strong></p>
                <p>Début : 17 mars</p>
                <p>🎓 <strong>Mai 2025</strong></p>
                <p>Début : 19 mai</p>
                <p>🎓 <strong>Septembre 2025</strong></p>
                <p>Début : 15 septembre</p>
                <p><small>15 places maximum par session</small></p>
              </InfoCard>
              
              <InfoCard>
                <h3>Contactez-nous</h3>
                <p>Des questions sur la formation ?</p>
                <p>Notre équipe pédagogique vous répond :</p>
                <p>📞 +33 6 01 48 53 83</p>
                <p>✉️ contact@institutgenevoisdeformation.fr</p>
              </InfoCard>
              
              <InfoCard>
                <h3>Financement</h3>
                <p>Plusieurs options disponibles :</p>
                <ul>
                  <li>Paiement comptant</li>
                  <li>3x sans frais</li>
                  <li>6x avec frais réduits</li>
                  <li>CPF (nous consulter)</li>
                </ul>
              </InfoCard>
            </Sidebar>
          </Grid>
        </Container>
      </ContentSection>
    </PageWrapper>
  );
};

export default AssistantMedical;